import { BehaviorSubject, Observable } from "rxjs";
import { I18nHelper, Translations } from "../../Lib/Helper/I18nHelper";
import { AxPEventHelper } from "./EventHelper";
import { InteractionHelper } from "./InteractionHelper";
import { RenderHelper } from "./RenderHelper";

export enum Mode {
    Inline,
    Endless
}

export class Options {
    StartOfWeek: number = 1;
    Lang: string = 'en';
    StartDate: moment.Moment;
    EndDate?: moment.Moment;

    Target: string | HTMLElement = '#ax-datepicker';
    Mode: Mode;

    LegendTarget?: string;
    BlockedDates?: moment.Moment[];
    AvailableDates?: moment.Moment[];
    OnlySelectDates?: boolean = false;
    DayRange?: number;
    monthsShown?: number = 2;
    scrollTargetQuerySelector?: string = null

    OnChange?: (startDate: moment.Moment, endDate: moment.Moment) => Promise<void>;
    AfterChange?: (startDate: moment.Moment, endDate: moment.Moment) => void;
};

export class AxDatePicker {
    public Options: Options;
    public Cells: Cell[];

    public Today: moment.Moment = moment();

    public RootElement: HTMLElement;
    public monthsShown: number = 2;

    public CurrentDate: moment.Moment;
    public CurrentStartDate: moment.Moment;
    public CurrentEndDate: moment.Moment;

    public parentElement: HTMLElement;

    public RenderEndless = false;

    constructor(public RenderH = new RenderHelper(),
        public EventHelper = new AxPEventHelper(),
        public ActionHelper: InteractionHelper = new InteractionHelper()) { }

    public ActivationSubject = new BehaviorSubject<Cell>(null);

    Init(options?: Options) {
        if (options != null) {
            this.Options = options;
        }

        //@ts-ignore
        let potentialTargets = $(this.Options.Target);
        if (potentialTargets.length > 0) {
            this.parentElement = potentialTargets[0];
        }

        this.monthsShown = this.Options.monthsShown ?? this.monthsShown;

        moment.lang(this.Options.Lang);
        moment.updateLocale(this.Options.Lang, {
            week: {
                dow: this.Options.StartOfWeek
            }
        });

        this.Today = moment();
        this.Today.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

        this.CurrentDate = (moment(this.Options.StartDate) || moment());

        this.EventHelper.Init(this);

        this.RenderH.Init(this);
    }

    setDate(date: moment.Moment, informSubscribers?: boolean) {
        this.CurrentDate = date;
        informSubscribers = informSubscribers ?? true;

        if (this.CurrentStartDate == null) return;

        if (this.CurrentStartDate.month() !== date.month()) {
            this.EventHelper.setDate(date);
        }
        this.ActionHelper.ActivateDay(this.ActionHelper.findCell(date, this), this, informSubscribers);
    }

    resetDateSelection() {
        this.ActionHelper.unsetDate(this, true);
    }

    DismissDatePicker() {
        $(this.parentElement).html("");
    }

    BlockDate(date: moment.Moment) {
        this.ActionHelper.BlockDate(date, this);
    }

    UnblockDate(date: moment.Moment) {
        this.ActionHelper.UnblockDate(date, this);
    }

    AddText(date: moment.Moment, text: string) {
        this.ActionHelper.AddText(date, text, this);
    }

    RemoveText(date: moment.Moment, removeCache?: boolean) {
        this.ActionHelper.RemoveText(date, this, removeCache || false);
    }

    RemoveAllText(removeCache?: boolean) {
        this.ActionHelper.RemoveAllText(this, removeCache || false);
    }

    FillEmptyText() {
        this.ActionHelper.FillEmptyText(this);
    }

    CleanUpCells() {
        this.Cells = this.Cells.filter(x => this.RootElement.contains(x.Element));
    }


    public AfterRender() {
        if (this.Options.Mode == Mode.Endless) {
            if (this.Options.scrollTargetQuerySelector != null) {
                document.querySelector(this.Options.scrollTargetQuerySelector).addEventListener("scroll", ev => {
                    this.HeightCheck();
                });
            } else {
                this.parentElement.addEventListener("scroll", ev => {
                    this.HeightCheck();
                });
            }


            window.addEventListener("resize", ev => {
                this.HeightCheck();
            });

            this.HeightCheck();
        }
    }

    public HeightCheck() {
        //TBH I have no idea what I'm doing but I think it works
        if (this.Options.Mode == Mode.Endless) {
            let scrollTarget: HTMLElement;

            if (this.Options.scrollTargetQuerySelector != null) {
                scrollTarget = document.querySelector(this.Options.scrollTargetQuerySelector);
            } else {
                scrollTarget = this.parentElement;
            }

            if ((this.Options.EndDate == null || this.CurrentDate <= this.Options.EndDate) && !this.RenderEndless && (Math.ceil(scrollTarget.offsetHeight + scrollTarget.scrollTop)) >= this.RootElement.offsetHeight) {
                this.RenderEndless = true;
                this.ActionHelper.Next(this, (a, b) => {
                    for (let month of a) {
                        this.RootElement.appendChild(month);
                    }
                    if (this.Options.AfterChange != null) {
                        this.Options.AfterChange(this.CurrentStartDate, this.CurrentEndDate);
                    }
                    this.RenderEndless = false;
                });
            }
        }
    }
}

export interface Cell {
    Element: HTMLElement;
    Active: boolean;
    InRange: boolean;
    Date: moment.Moment;
    Text: string;
    DayRange: number;
}